<template>
  <section class="mt-10">
    <div class="bg-white dark:bg-gray-800 relative shadow-md sm:rounded-lg overflow-hidden">
      <div class="border-b dark:border-gray-700 mx-4">
        <div class="flex items-center justify-between space-x-4 pt-3">
          <div class="flex-1 flex items-center space-x-3">
            <h5 class="dark:text-white font-semibold">
              Announcements
            </h5>
          </div>
        </div>
        <div class="flex flex-col-reverse md:flex-row items-center justify-between md:space-x-4 py-3">
          <div class="w-full lg:w-2/3 flex flex-col space-y-3 md:space-y-0 md:flex-row md:items-center">
            <form class="w-full md:max-w-sm flex-1 md:mr-4">
              <label
                class="text-sm font-medium text-gray-900 sr-only dark:text-white"
                for="announcement-search"
              >Search</label>
              <input
                id="announcement-search"
                v-model="searchQuery"
                class="block w-full p-2 pl-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                placeholder="Search announcements..."
                type="search"
              >
            </form>
          </div>
          <div
            class="w-full md:w-auto flex flex-col md:flex-row mb-3 md:mb-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0"
          >
            <button
              class="flex items-center justify-center text-white bg-black hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-4 py-2 dark:bg-primary-600 dark:hover:bg-primary-700 focus:outline-none dark:focus:ring-primary-800"
              type="button"
              @click="openModal('newAnnouncement')"
            >
              <svg
                class="h-3.5 w-3.5 mr-2"
                fill="none"
                height="12"
                viewBox="0 0 12 12"
                width="12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 0.75V11.25M0.75 6H11.25"
                  stroke="white"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                />
              </svg>
              Add New Announcement
            </button>
            <Modal
              :is-visible="modals.newAnnouncement"
              modal-id="newAnnouncement"
              @update:is-visible="value => modals.newAnnouncement = value"
            >
              <template #body>
                <div class="p-4">
                  <div class="flex flex-col items-center justify-center">
                    <h2 class="block text-2xl font-bold text-gray-800">
                      Add New Announcement
                    </h2>
                    <p class="mt-2 text-sm text-gray-600">
                      Publish a new message to members.
                    </p>
                  </div>
                  <div class="mt-6 grid gap-6 md:grid-cols-2">
                    <div>
                      <label class="block mb-2 text-sm font-medium text-gray-700">Title</label>
                      <input
                        v-model="form.title"
                        class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                        placeholder="Announcement title"
                        required
                        type="text"
                      >
                    </div>
                    <div class="md:col-span-2">
                      <label class="block mb-2 text-sm font-medium text-gray-700">Message</label>
                      <textarea
                        v-model="form.body"
                        class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                        placeholder="Write the announcement"
                        rows="3"
                        required
                      />
                    </div>
                  </div>
                </div>
              </template>
              <template #footer>
                <div class="flex items-center">
                  <button
                    class="w-full py-3 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-semibold rounded-es-xl border border-transparent bg-gray-100 text-gray-800 hover:bg-gray-200"
                    type="button"
                    @click="closeModal('newAnnouncement')"
                  >
                    Cancel
                  </button>
                  <button
                    class="w-full py-3 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-semibold rounded-ee-xl border border-transparent bg-black text-white hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none"
                    type="button"
                    @click="createAnnouncement"
                  >
                    Publish
                  </button>
                </div>
              </template>
            </Modal>
          </div>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th
                class="px-4 py-3"
                scope="col"
              >
                Title
              </th>
              <th
                class="px-4 py-3"
                scope="col"
              >
                Message
              </th>
              <th
                class="px-4 py-3"
                scope="col"
              >
                Created
              </th>
              <th
                class="px-4 py-3"
                scope="col"
              >
                <span class="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody v-if="loading">
            <tr class="border-b dark:border-gray-600">
              <td
                class="px-4 py-4 text-sm text-gray-500"
                colspan="4"
              >
                Loading announcements...
              </td>
            </tr>
          </tbody>
          <tbody v-else-if="filteredAnnouncements.length === 0">
            <tr class="border-b dark:border-gray-600">
              <td
                class="px-4 py-4 text-sm text-gray-500"
                colspan="4"
              >
                {{ searchQuery ? 'No announcements match your search.' : 'No announcements yet.' }}
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr
              v-for="announcement in filteredAnnouncements"
              :key="announcement._id"
              class="border-b dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <th
                class="px-4 py-3 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                scope="row"
              >
                {{ announcement.title }}
              </th>
              <td class="px-4 py-3">
                {{ announcement.body }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                {{ formatDate(announcement.createdAt) }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                <div class="flex items-center gap-2">
                  <router-link
                    class="flex items-center justify-center w-10 h-10 text-gray-700 bg-white border border-gray-300 rounded-lg md:w-10 focus:outline-none hover:bg-gray-100 hover:text-primary-700 focus:z-10 focus:ring-4 focus:ring-gray-200"
                    :to="`/administrator/announcements/${announcement._id}`"
                    aria-label="Edit announcement"
                    title="Edit"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g
                        id="SVGRepo_bgCarrier"
                        stroke-width="0"
                      />
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <g id="SVGRepo_iconCarrier">
                        <g>
                          <g>
                            <g>
                              <g>
                                <g>
                                  <path
                                    d="M20,16v4a2,2,0,0,1-2,2H4a2,2,0,0,1-2-2V6A2,2,0,0,1,4,4H8"
                                    fill="none"
                                    stroke="#000000"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                  />
                                  <polygon
                                    fill="none"
                                    points="12.5 15.8 22 6.2 17.8 2 8.3 11.5 8 16 12.5 15.8"
                                    stroke="#000000"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                  />
                                </g>
                              </g>
                            </g>
                          </g>
                        </g>
                      </g>
                    </svg>
                  </router-link>
                  <button
                    class="flex items-center justify-center w-10 h-10 text-red-500 bg-white border border-red-400 rounded-lg md:w-10 focus:outline-none hover:bg-gray-100 hover:text-red-600 focus:z-10 focus:ring-4 focus:ring-gray-200"
                    type="button"
                    aria-label="Delete announcement"
                    title="Delete"
                    @click="deleteAnnouncement(announcement._id)"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g
                        id="SVGRepo_bgCarrier"
                        stroke-width="0"
                      />
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <g id="SVGRepo_iconCarrier">
                        <path
                          d="M4 7H20"
                          stroke="#ff5c5c"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                        />
                        <path
                          d="M6 10L7.70141 19.3578C7.87432 20.3088 8.70258 21 9.66915 21H14.3308C15.2974 21 16.1257 20.3087 16.2986 19.3578L18 10"
                          stroke="#ff5c5c"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                        />
                        <path
                          d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z"
                          stroke="#ff5c5c"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                        />
                      </g>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <nav
        aria-label="Table navigation"
        class="flex flex-col md:flex-row justify-between items-start md:items-center space-y-3 md:space-y-0 p-4"
      >
        <span class="text-sm font-normal text-gray-500 dark:text-gray-400">
          Showing <span class="font-semibold text-gray-900 dark:text-white">1-10</span> of <span class="font-semibold text-gray-900 dark:text-white">{{ filteredAnnouncements.length }}</span>
        </span>
        <ul class="inline-flex items-stretch -space-x-px">
          <li>
            <a
              class="flex items-center justify-center h-full py-1.5 px-3 ml-0 text-gray-500 bg-white rounded-l-lg border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              href="#"
            >
              <span class="sr-only">Previous</span>
              <svg
                aria-hidden="true"
                class="w-5 h-5"
                fill="currentColor"
                viewbox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  clip-rule="evenodd"
                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                  fill-rule="evenodd"
                />
              </svg>
            </a>
          </li>
          <li>
            <a
              class="flex items-center justify-center text-sm py-2 px-3 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              href="#"
            >0</a>
          </li>
          <li>
            <a
              aria-current="page"
              class="flex items-center justify-center text-sm z-10 py-2 px-3 leading-tight text-primary-600 bg-primary-50 border border-primary-300 hover:bg-primary-100 hover:text-primary-700 dark:border-gray-700 dark:bg-gray-700 dark:text-white"
              href="#"
            >1</a>
          </li>
          <li>
            <a
              class="flex items-center justify-center text-sm py-2 px-3 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              href="#"
            >...</a>
          </li>
          <li>
            <a
              class="flex items-center justify-center text-sm py-2 px-3 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              href="#"
            >1</a>
          </li>
          <li>
            <a
              class="flex items-center justify-center h-full py-1.5 px-3 leading-tight text-gray-500 bg-white rounded-r-lg border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              href="#"
            >
              <span class="sr-only">Next</span>
              <svg
                aria-hidden="true"
                class="w-5 h-5"
                fill="currentColor"
                viewbox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  clip-rule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  fill-rule="evenodd"
                />
              </svg>
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </section>
</template>

<script lang="ts">
import {defineComponent, watchEffect} from 'vue';
import axios from 'axios';
import {Announcement} from '@/types';
import {useHead} from "@vueuse/head";
import Modal from '@/components/Modal.vue';

export default defineComponent({
  name: 'Announcements',
  components: {
    Modal,
  },
  setup() {
    watchEffect(() => {
      useHead({
        title: 'Announcements',
      });
    });
  },
  data() {
    return {
      announcements: [] as Announcement[],
      loading: false,
      error: '' as string,
      searchQuery: '',
      modals: {
        newAnnouncement: false,
      },
      form: {
        title: '',
        body: '',
      },
    };
  },
  computed: {
    filteredAnnouncements(): Announcement[] {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return this.announcements;
      return this.announcements.filter((announcement) =>
        [announcement.title, announcement.body]
          .filter(Boolean)
          .some((value) => value!.toLowerCase().includes(query))
      );
    },
  },
  async mounted() {
    await this.fetchAnnouncements();
  },
  methods: {
    openModal(modalId: 'newAnnouncement') {
      this.modals[modalId] = true;
    },
    closeModal(modalId: 'newAnnouncement') {
      this.modals[modalId] = false;
    },
    async fetchAnnouncements() {
      this.loading = true;
      try {
        const response = await axios.get<Announcement[]>('/announcements');
        this.announcements = response.data;
      } catch (err) {
        console.error(err);
        this.error = 'Failed to fetch announcements.';
      } finally {
        this.loading = false;
      }
    },
    async createAnnouncement() {
      this.error = '';
      try {
        const response = await axios.post<Announcement>('/announcements', {
          title: this.form.title,
          body: this.form.body,
        });
        this.announcements = [response.data, ...this.announcements];
        this.form = {title: '', body: ''};
        this.closeModal('newAnnouncement');
      } catch (err) {
        console.error(err);
        this.error = 'Failed to create announcement.';
      }
    },
    async deleteAnnouncement(id: string) {
      this.error = '';
      try {
        await axios.delete(`/announcements/${id}`);
        this.announcements = this.announcements.filter((item) => item._id !== id);
      } catch (err) {
        console.error(err);
        this.error = 'Failed to delete announcement.';
      }
    },
    formatDate(value?: string) {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(date);
    },
  },
});
</script>
